"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.text = void 0;
const create_fn_1 = require("../utils/create-fn");
const get_arg_1 = require("../utils/get-arg");
const to_ordinal_1 = require("../utils/to-ordinal");
/*
## fn.text.extract

Returns a substring from a string starting at the position specified in start and continuing for the number of characters provided in count

### Arguments

1. (string) The string to extract from.
2. (number) Zero-based index at which to start extraction.
3. (number) The number of characters to extract.

Returns the substring. (string)
*/
const extract = (0, create_fn_1.createFn)("fn.text.extract", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const start = (0, get_arg_1.getArgNumber)(args, 2, context);
    const count = (0, get_arg_1.getArgNumber)(args, 3, context);
    return value.substring(start, start + count);
});
/*
## fn.text.left

Returns a Value object containing the leftmost charCount number of characters

### Arguments

1. (string) The string to extract from.
2. (number) The number of characters to extract.

Returns the substring. (string)
*/
const left = (0, create_fn_1.createFn)("fn.text.left", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const count = (0, get_arg_1.getArgNumber)(args, 2, context);
    return value.substring(0, count);
});
/*
## fn.text.right

Returns a substring of the original string, starting from the rightmost character a counting back the number of characters specified in the string argument

### Arguments

1. (string) The string to extract from.
2. (number) The number of characters to extract.

Returns the substring. (string)
*/
const right = (0, create_fn_1.createFn)("fn.text.right", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const count = (0, get_arg_1.getArgNumber)(args, 2, context);
    return value.substring(value.length - count);
});
/*
## fn.text.concat

Concatenates multiple strings together

### Arguments

1-Infinity. (string) The strings to concatenate.

Returns the concatenated string. (string)
*/
const concat = (0, create_fn_1.createFn)("fn.text.concat", (args, context) => {
    let result = "";
    for (let i = 1; i <= args.length; i++) {
        const value = (0, get_arg_1.getArgUnknown)(args, i, context);
        if (value === undefined) {
            continue;
        }
        if (typeof value !== "string" && typeof value !== "number") {
            throw new Error(`Expected the ${(0, to_ordinal_1.toOrdinal)(i)} arg to be a string or number, instead "${value}" was received which is of type ${typeof value}`);
        }
        result += value;
    }
    return result;
});
/*
## fn.text.contains

Returns a `true` Value if the source string contains the search string

### Arguments

1. (string) The source string.
2. (string) The search string.

Returns a boolean. (boolean)
*/
const contains = (0, create_fn_1.createFn)("fn.text.contains", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const search = (0, get_arg_1.getArgString)(args, 2, context);
    return value.includes(search);
});
/*
## fn.text.startswith

Returns a `true` Value if the source string starts with the search string

### Arguments

1. (string) The source string.
2. (string) The search string.

Returns a boolean. (boolean)
*/
const startswith = (0, create_fn_1.createFn)("fn.text.startswith", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const search = (0, get_arg_1.getArgString)(args, 2, context);
    return value.startsWith(search);
});
/*
## fn.text.endswith

Returns a `true` Value if the source string ends with the search string

### Arguments

1. (string) The source string.
2. (string) The search string.

Returns a boolean. (boolean)
*/
const endswith = (0, create_fn_1.createFn)("fn.text.endswith", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const search = (0, get_arg_1.getArgString)(args, 2, context);
    return value.endsWith(search);
});
/*
## fn.text.repeatstring

Repeats the string provided in the first argument the number of times specified in the second argument

### Arguments

1. (string) The string to repeat.
2. (number) The number of times to repeat the string.

Returns the repeated string. (string)
*/
const repeatstring = (0, create_fn_1.createFn)("fn.text.repeatstring", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const count = (0, get_arg_1.getArgNumberOrUndefined)(args, 2, context);
    return value.repeat(count ?? 0);
});
/*
## fn.text.length

Returns the length of a string

### Arguments

1. (string) The string to get the length of.

Returns the length of the string. (number)
*/
const length = (0, create_fn_1.createFn)("fn.text.length", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    return value.length;
});
/*
## fn.text.trim

Removes leading and trailing whitespace from a string

### Arguments

1. (string) The string to trim.

Returns the trimmed string. (string)
*/
const trim = (0, create_fn_1.createFn)("fn.text.trim", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    return value.trim();
});
/*
## fn.text.normalizespace

Replaces all sequences of whitespace characters with a single space

### Arguments

1. (string) The string to normalize.

Returns the normalized string. (string)
*/
const normalizespace = (0, create_fn_1.createFn)("fn.text.normalizespace", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    return value.replace(/\s{2,}/g, " ").trim();
});
/*
## fn.text.before

Returns the substring before the first occurrence of the search string

### Arguments

1. (string) The source string.
2. (string) The search string.

Returns the substring. (string)
*/
const before = (0, create_fn_1.createFn)("fn.text.before", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const search = (0, get_arg_1.getArgString)(args, 2, context);
    const index = value.indexOf(search);
    if (index === -1) {
        return value;
    }
    return value.substring(0, index);
});
/*
## fn.text.after

Returns the substring after the first occurrence of the search string

### Arguments

1. (string) The source string.
2. (string) The search string.

Returns the substring. (string)
*/
const after = (0, create_fn_1.createFn)("fn.text.after", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const search = (0, get_arg_1.getArgString)(args, 2, context);
    const index = value.indexOf(search);
    if (index === -1) {
        return "";
    }
    return value.substring(index + search.length);
});
/*
## fn.text.containsany

Returns a `true` Value if the source string contains any of the search strings

### Arguments

1. (string) The source string.
2. (array) The search strings.

Returns a boolean. (boolean)
*/
const containsany = (0, create_fn_1.createFn)("fn.text.containsany", (args, context) => {
    const value = (0, get_arg_1.getArgString)(args, 1, context);
    const searchStrings = (0, get_arg_1.getArgArray)(args, 2, context);
    return searchStrings.some((search) => typeof search === "string" ? value.includes(search) : false);
});
/*
## fn.text.split

Splits a string into an array of strings using a separator

### Arguments

1. (string) The string to split.
2. (string) The separator to use.

Returns a list of strings. (array)
*/
const split = (0, create_fn_1.createFn)("fn.list.split", (args, context) => {
    const value = (0, get_arg_1.getArgStringOrUndefined)(args, 1, context);
    if (!value) {
        return [];
    }
    const separator = (0, get_arg_1.getArgString)(args, 2, context);
    return value.split(separator);
});
/*
## fn.text.replace

Replaces all occurrences of a substring in a string with another substring

### Arguments

1. (string) The source string.
2. (string) The search string.
3. (string) The replacement string.

Returns the modified string. (string)
*/
const replace = (0, create_fn_1.createFn)("fn.text.replace", (args, context) => {
    const value = (0, get_arg_1.getArgStringOrUndefined)(args, 1, context);
    if (!value) {
        return "";
    }
    const search = (0, get_arg_1.getArgString)(args, 2, context);
    const replacement = (0, get_arg_1.getArgString)(args, 3, context);
    return value.replaceAll(search, replacement);
});
exports.text = {
    extract,
    mid: extract,
    left,
    right,
    concat,
    contains,
    startswith,
    endswith,
    repeatstring,
    length,
    trim,
    normalizespace,
    before,
    after,
    split,
    replace,
    containsany,
};
//# sourceMappingURL=text.js.map